// src/pwa/prompt.ts: the install prompt in the live page (docs/DECISIONS.md, "Install prompt"). It keeps this device's
// memory (src/lib/storage.ts), the trigger that asked, Chrome's kept install event (src/lib/pwa.ts, which also holds
// back Chrome's own banner), and records the funnel events; the rules are src/pwa/install.ts. Nothing here draws: the
// card renders in src/pwa/InstallSlot.tsx, the Settings row and /install read useInstallPrompt(). Inert until
// startInstall() runs with a PWA_MODE that allows installing: with PWA_MODE off no trigger, launch or event does a thing.
//   raiseInstallTrigger(t)  from the real moments: a message sent to the team (Messages), and right after Start Your
//                           Build's Sent page (the welcome sheet put away, or the sent page's code opening the board).
//                           The second visit is raised here, on a household member's first visit of a later day.
//   useInstallPrompt()      what the designed UI calls: { available, platform, os, standalone, offered, card,
//                           install(from), browserHref, guide, pageHref, pings }. The card is null until a trigger
//                           asked and every rule allows.
//   startInstall(mode)      after boot (src/main.tsx): records a launch of the app once, notes the household visit,
//                           and records installs.
import { computed, effect, signal } from "@preact/signals";
import type { FunnelEventName } from "../../shared/api";
import { parsePwaMode, pwaAllows, type PwaMode } from "../../shared/pwa";
import { route } from "../app/router";
import { heldInstallPrompt, inApp, takeInstallPrompt } from "../lib/pwa";
import { local, session } from "../lib/storage";
import { track } from "../start/events";
import { welcomeSheetUp } from "../start/sheet-flag";
import { me, tenant } from "../state/session";
import {
  actionFor, afterInstalled, afterNever, afterNotNow, afterShown, cardBlocker, chromeIntentHref, detectOs, detectPlatform, EMPTY_MEMORY, isLaunch,
  iosGuideOf, isPhoneOrTablet, launchOf, localDay, mayShowFor, noteVisit, offersInstall, parseMemory, pickTrigger, TRIGGER_TTL_MS, viewerOf,
  type BrowserInfo, type InstallMemory, type IosGuide, type InstallOs, type InstallOutcome, type InstallPlatform, type InstallSource, type InstallTrigger, type Launch,
  type PendingTrigger,
} from "./install";

/**
 * localStorage (hb:pwa:install): this device's memory. sessionStorage (hb:pwa:launched): this launch was recorded.
 * Safari deletes the memory after 7 days of use without a visit here, Don't ask again included (docs/DECISIONS.md).
 */
export const MEMORY_KEY = "pwa:install";
export const LAUNCH_KEY = "pwa:launched";

/** The card the design renders (src/pwa/InstallCard.tsx): what asked, where, and the family's four answers. */
export type InstallCard = {
  trigger: InstallTrigger;
  platform: InstallPlatform;
  /** The main button was tapped already (a sheet shown again after a move resumes the steps). */
  accepted: boolean;
  /** Call once the card is on screen: it counts toward the limits and records pwa_card_shown. Until then nothing counts. */
  shown(): void;
  /**
   * The main button. Android: Chrome's dialog opens (call it straight from the tap) and resolves installed or
   * dismissed; the card closes either way. iOS: resolves guide (show the steps). In-app browsers: resolves
   * open-in-browser. The card stays open for those two until close().
   */
  accept(): Promise<InstallOutcome>;
  /** Not now (or any other way of putting it away before accepting): snoozes. */
  notNow(): void;
  /** Don't ask again. */
  never(): void;
  /** Closes the card after accept (the steps are done); before accept it counts as Not now. */
  close(): void;
};

export type InstallPromptState = {
  /** PWA_MODE allows installing and this builder has an app. */
  available: boolean;
  platform: InstallPlatform;
  /** For in-app browsers and unsupported ones: "Open in Safari" (ios) or "Open in Chrome" (android). */
  os: InstallOs;
  /** Running as the installed app. */
  standalone: boolean;
  /** The Settings row and /install have something to offer (never in the app, on a desktop or an unsupported browser). */
  offered: boolean;
  /** The card due now, or null. */
  card: InstallCard | null;
  /** The Settings row's or /install's main button (Android opens Chrome's dialog: call it straight from the tap). */
  install(from: "settings" | "page"): Promise<InstallOutcome>;
  /** In an Android in-app browser, a link that opens this page in Chrome; null elsewhere. */
  browserHref: string | null;
  /** How the iPhone steps read (Safari 26's "•••", earlier Safari, or Chrome and Edge); null off iOS. */
  guide: IosGuide | null;
  /** This page's address without its fragment, for "Copy Link" in an iOS in-app browser; null while unavailable. */
  pageHref: string | null;
  /**
   * PWA_MODE allows push: the installed app can ping the family when the team replies. Until then the design must not
   * promise a ping (docs/DECISIONS.md, "Install prompt").
   */
  pings: boolean;
};

type Runtime = { mode: PwaMode; browser: BrowserInfo; standalone: boolean; now: () => number; win: Window };
type OpenCard = { trigger: InstallTrigger; platform: InstallPlatform; accepted: boolean };

const runtime = signal<Runtime | null>(null);
/**
 * Held: every card but the demo tour's own waits (the demo tour holds it until its app stop, src/demo/tour.ts). A
 * held trigger is not dropped; unshown, it lapses as any other.
 */
export const installHeld = signal(false);
const memory = signal<InstallMemory>({ ...EMPTY_MEMORY });
const pending = signal<PendingTrigger | null>(null);
/** The card on screen (shown() was called) until the family answers it. */
const openCard = signal<OpenCard | null>(null);
/** Chrome's dialog is open (or was accepted) from here: pwa_installed says where the install began. */
let installing: { from: InstallSource; platform: InstallPlatform } | null = null;
let stopRun: (() => void) | null = null;

// ------------------------------------------------------------------ memory and events

function readStored(): InstallMemory {
  return parseMemory(local.getJSON<unknown>(MEMORY_KEY, null));
}

/**
 * Applies a rule to the stored memory (read fresh: another tab may have written since) and keeps the result. Where
 * storage is blocked (a private window) the page's own copy carries on, so this visit's answers still hold.
 */
function remember(rule: (m: InstallMemory) => InstallMemory): InstallMemory {
  const raw = local.getJSON<unknown>(MEMORY_KEY, null);
  const next = rule(raw === null ? memory.peek() : parseMemory(raw));
  local.setJSON(MEMORY_KEY, next);
  memory.value = next;
  return next;
}

/** Enum-only props, no form variant (src/start/events.ts; the Worker keeps only each event's own props). */
function emit(event: FunnelEventName, props: Record<string, string | number | boolean | undefined>): void {
  if (!runtime.peek()) return;
  const clean: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(props)) if (v !== undefined) clean[k] = v;
  track(event, { props: clean }, null);
}

// ------------------------------------------------------------------ what the page shows

const platform = computed<InstallPlatform>(() => {
  const r = runtime.value;
  if (!r) return "unsupported";
  return detectPlatform({ ...r.browser, canPrompt: !!heldInstallPrompt.value });
});

const hasApp = computed(() => !!tenant.value?.brand.app);

function closeCard(): void {
  openCard.value = null;
  pending.value = null;
}

function cardFor(trigger: InstallTrigger, where: InstallPlatform, accepted: boolean): InstallCard {
  return {
    trigger,
    platform: where,
    accepted,
    shown: () => {
      const r = runtime.peek();
      if (!r || openCard.peek()) return;
      const m = remember((x) => afterShown(x, r.now()));
      openCard.value = { trigger, platform: where, accepted: false };
      pending.value = null;
      emit("pwa_card_shown", { trigger, platform: where, from: "card", n: m.shows });
    },
    accept: () => start("card"),
    notNow: () => dismiss("not_now"),
    never: () => dismiss("never"),
    close: () => {
      if (openCard.peek()?.accepted) closeCard();
      else dismiss("not_now");
    },
  };
}

function dismiss(choice: "not_now" | "never"): void {
  const r = runtime.peek();
  const open = openCard.peek();
  if (!r || !open) return;
  remember((m) => (choice === "never" ? afterNever(m) : afterNotNow(m, r.now())));
  emit("pwa_card_dismissed", { trigger: open.trigger, platform: open.platform, from: "card", choice });
  closeCard();
}

const card = computed<InstallCard | null>(() => {
  const r = runtime.value;
  if (!r) return null;
  const open = openCard.value;
  const trigger = open?.trigger ?? pending.value?.trigger;
  if (!trigger) return null;
  // A card on screen keeps the platform it was shown for (Chrome's event is used up while its dialog is open).
  const where = open?.platform ?? platform.value;
  const here = route.value;
  const blocked = cardBlocker({
    mode: r.mode,
    hasApp: hasApp.value,
    viewer: viewerOf(me.value),
    platform: where,
    mobile: isPhoneOrTablet(r.browser),
    standalone: r.standalone,
    route: here.name,
    today: localDay(r.now()),
    firstDay: memory.value.firstDay,
    trigger,
    welcomeSheet: welcomeSheetUp(here.params.pid, here.query),
  });
  // A card on screen stays until answered (hidden while its page or viewer does not qualify, on Sort for example, or
  // while the board's welcome sheet is up).
  if (blocked) return null;
  if (!open && installHeld.value && trigger !== "demo_tour") return null;
  if (!open && !mayShowFor(trigger, memory.value, r.now())) return null;
  return cardFor(trigger, where, open?.accepted ?? false);
});

/**
 * The main action, from the card, the Settings row or /install. On Android nothing is awaited before prompt(), so
 * Chrome still sees the tap.
 */
async function start(from: "card" | "settings" | "page"): Promise<InstallOutcome> {
  const r = runtime.peek();
  if (!r || r.standalone) return "unavailable";
  const open = from === "card" ? openCard.peek() : null;
  if (from === "card" && !open) return "unavailable";
  const where = platform.peek();
  const action = actionFor(where);
  if (action === "unavailable") return "unavailable";
  const trigger = open?.trigger;
  if (action !== "prompt") {
    // The steps (or "open the real browser") again from the same card count once.
    if (!open?.accepted) emit("pwa_card_accepted", { trigger, platform: where, from });
    if (open) openCard.value = { ...open, accepted: true };
    return action;
  }
  const e = takeInstallPrompt();
  if (!e) return "unavailable";
  emit("pwa_card_accepted", { trigger, platform: where, from });
  installing = { from, platform: where };
  let outcome: "accepted" | "dismissed";
  try {
    await e.prompt();
    outcome = (await e.userChoice).outcome;
  } catch {
    // Chrome refused (no tap, or the event was already used): nothing opened.
    installing = null;
    if (open) closeCard();
    return "unavailable";
  }
  if (open) closeCard();
  if (outcome === "accepted") {
    remember(afterInstalled);
    return "installed";
  }
  installing = null;
  emit("pwa_card_dismissed", { trigger, platform: where, from, choice: "native_cancel" });
  if (from === "card") remember((m) => afterNotNow(m, r.now()));
  return "dismissed";
}

/** This page's address without its fragment (emailed links keep tokens there), for the link into Chrome. */
function hereHref(r: Runtime): string {
  const here = route.value;
  const qs = here.query.toString();
  return `${r.win.location.origin}${here.path}${qs ? `?${qs}` : ""}`;
}

const state = computed<InstallPromptState>(() => {
  const r = runtime.value;
  const where = platform.value;
  const available = !!r && pwaAllows(r.mode, "install") && hasApp.value;
  const standalone = r?.standalone ?? false;
  return {
    available,
    platform: where,
    os: r ? detectOs(r.browser) : "other",
    standalone,
    offered: !!r && offersInstall({ mode: r.mode, hasApp: hasApp.value, platform: where, standalone }),
    card: card.value,
    install: (from) => start(from),
    browserHref: r && available && !standalone ? chromeIntentHref(hereHref(r), r.browser) : null,
    guide: r ? iosGuideOf(where, r.browser.ua) : null,
    pageHref: r && available && !standalone ? hereHref(r) : null,
    pings: !!r && pwaAllows(r.mode, "push"),
  };
});

/**
 * What the designed UI calls. A signal read, not a hook with state: the component re-renders when any of it changes,
 * and outside a component it answers for this moment. Inert (nothing offered, no card) while PWA_MODE is off.
 */
export function useInstallPrompt(): InstallPromptState {
  return state.value;
}

/**
 * The Settings row's way to /install (?from=settings): its tap is already recorded (pwa_card_accepted from settings),
 * so the visit is no page view and /install's count stays the email footers' and QR cards'.
 */
export const FROM_SETTINGS = { from: "settings" } as const;

/** A visit to /install (an email footer's or a QR card's link): pwa_card_shown from "page", with this browser. */
export function notePageView(): void {
  if (route.peek().query.get("from") === FROM_SETTINGS.from) return;
  emit("pwa_card_shown", { platform: platform.peek(), from: "page" });
}

// ------------------------------------------------------------------ triggers

/**
 * A moment that may earn the card. The first one wins, except that an unshown second visit gives way to a message or
 * Start Your Build (install.ts, pickTrigger); a card already on screen is not replaced.
 */
export function raiseInstallTrigger(trigger: InstallTrigger): void {
  const r = runtime.peek();
  if (!r || openCard.peek()) return;
  const next = pickTrigger(pending.peek(), trigger, r.now());
  if (next === pending.peek()) return;
  pending.value = next;
  // Unshown, it lapses: a card never turns up long after its moment.
  setTimeout(() => {
    if (pending.peek() === next) pending.value = null;
  }, TRIGGER_TTL_MS);
}

// ------------------------------------------------------------------ start

export type InstallDeps = {
  /** How this page was opened (src/main.tsx reads it before the router drops ?src=app). */
  launch?: Launch;
  win?: Window;
  browser?: BrowserInfo;
  now?: () => number;
};

/**
 * After boot. PWA_MODE off (or missing): nothing, and everything above stays inert. Otherwise: records a launch of the
 * app (once per launch, and an app launch means installed), notes the household member's visit (a later day raises
 * second_visit), keeps this device's memory in step with other tabs, and records installs. Returns a stop function.
 */
export function startInstall(mode: PwaMode | undefined, deps: InstallDeps = {}): () => void {
  stopRun?.();
  const parsed = parsePwaMode(mode);
  if (!pwaAllows(parsed, "install") || typeof window === "undefined") return () => undefined;
  const win = deps.win ?? window;
  const now = deps.now ?? Date.now;
  const launch = deps.launch ?? launchOf(win.location.search, inApp());
  const browser = deps.browser ?? { ua: win.navigator.userAgent, touchPoints: win.navigator.maxTouchPoints ?? 0 };
  memory.value = readStored();
  runtime.value = { mode: parsed, browser, standalone: launch.standalone, now, win };

  if (isLaunch(launch) && session.get(LAUNCH_KEY) !== "1") {
    session.set(LAUNCH_KEY, "1");
    emit("pwa_launch_app", { display: launch.standalone ? "app" : "browser", src: launch.fromIcon ? "app" : "none", os: detectOs(browser) });
  }
  // The app shares storage with the browser on Android: the browser tab stops asking once the app has run.
  if (launch.standalone) remember(afterInstalled);

  let noted = false;
  const stopVisit = effect(() => {
    if (noted || viewerOf(me.value) !== "household") return;
    noted = true;
    let second = false;
    remember((m) => {
      const v = noteVisit(m, localDay(now()));
      second = v.secondVisit;
      return v.memory;
    });
    if (second) raiseInstallTrigger("second_visit");
  });

  const onStorage = (e: StorageEvent) => {
    if (e.key === null || e.key === `hb:${MEMORY_KEY}`) memory.value = readStored();
  };
  const onInstalled = () => {
    remember(afterInstalled);
    const via = installing ?? { from: "browser" as const, platform: detectPlatform({ ...browser, canPrompt: true }) };
    emit("pwa_installed", { platform: via.platform, from: via.from });
    installing = null;
    closeCard();
  };
  win.addEventListener("storage", onStorage);
  win.addEventListener("appinstalled", onInstalled);

  const stop = () => {
    stopVisit();
    win.removeEventListener("storage", onStorage);
    win.removeEventListener("appinstalled", onInstalled);
    runtime.value = null;
    pending.value = null;
    openCard.value = null;
    installing = null;
    if (stopRun === stop) stopRun = null;
  };
  stopRun = stop;
  return stop;
}
